import type {
  BufferedAction,
  Fighter,
  FighterId,
  FighterState,
  GameEvent,
  GameStatus,
  InputFrame,
  LedgeId,
  Platform,
  Projectile,
  StageLedge,
} from "./game-types";
import {
  hashDeterministicState,
  type FighterDiagnostics,
  type SimulationSnapshot,
} from "./determinism";
import {
  COMBO_RESET_FRAMES,
  comboScalingForStep,
  scaleComboDamage,
  type ComboScaling,
} from "./combo-scaling";
import { getFighterConfig } from "./fighter-config";
import { Vector2 } from "./vector";

export const WORLD_WIDTH = 1920;
export const WORLD_HEIGHT = 1080;
export const FIXED_STEP = 1 / 60;

const GRAVITY = 1650;
const MAX_FALL_SPEED = 920;
const MATCH_SECONDS = 180;
const GRAB_HOLD_SECONDS = 1.05;
const GRAB_INPUT_DELAY = 0.12;
const STANDARD_HIT_KNOCKBACK_SCALE = 0.5;
export const RECOVERY_INPUT_BUFFER_FRAMES = 5;
export const LEDGE_GRAB_INVULNERABILITY = 0.5;
export const LEDGE_ROLL_INVULNERABILITY = 0.36;
export const LEDGE_REGRAB_COOLDOWN = 0.25;
const LEDGE_GRAB_HORIZONTAL_RANGE = 62;
const LEDGE_GRAB_ABOVE_RANGE = 74;
const LEDGE_GRAB_BELOW_RANGE = 96;
const LEDGE_GRAB_MAX_RISE_SPEED = -120;
const LEDGE_CLIMB_DURATION = 0.34;
const LEDGE_ATTACK_DURATION = 0.52;
const LEDGE_ROLL_DURATION = 0.5;
const DEFAULT_CPU_SEED = 0x4f1bbcdc;
const EMPTY_INPUT: InputFrame = {
  moveX: 0,
  jumpPressed: false,
  attackPressed: false,
  specialPressed: false,
  grabPressed: false,
  shieldPressed: false,
  shieldHeld: false,
  dashHeld: false,
  downHeld: false,
};

export const PLATFORMS: Platform[] = [
  { id: "main", x1: 149, x2: 1775, y: 779, oneWay: true },
  { id: "left", x1: 280, x2: 628, y: 405, oneWay: true },
  { id: "right", x1: 1294, x2: 1614, y: 405, oneWay: true },
];

export const LEDGES: StageLedge[] = [
  {
    id: "main-left",
    platformId: "main",
    x: PLATFORMS[0]!.x1,
    y: PLATFORMS[0]!.y,
    inward: 1,
  },
  {
    id: "main-right",
    platformId: "main",
    x: PLATFORMS[0]!.x2,
    y: PLATFORMS[0]!.y,
    inward: -1,
  },
];

function isLedgeState(state: FighterState): boolean {
  return state.startsWith("ledge-");
}

function makeFighter(id: FighterId, x: number, facing: -1 | 1): Fighter {
  return {
    id,
    config: getFighterConfig(id),
    position: new Vector2(x, PLATFORMS[0]?.y ?? 735),
    previousPosition: new Vector2(x, PLATFORMS[0]?.y ?? 735),
    velocity: new Vector2(),
    facing,
    grounded: true,
    groundPlatform: "main",
    jumpsRemaining: 1,
    damage: 0,
    stocks: 4,
    shield: 100,
    state: "idle",
    actionTimer: 0,
    actionDuration: 0,
    actionInstance: -1,
    actionTriggered: false,
    attackConnected: false,
    invulnerability: 0,
    respawnTimer: 0,
    dropTimer: 0,
    airDodgeAvailable: true,
    flashTimer: 0,
    hitPulse: 0,
    bufferedAction: null,
    bufferedActionFrames: 0,
    comboCount: 0,
    comboRecoveryFrames: 0,
    comboActionInstance: -1,
    comboDamageScale: 1,
    comboKnockbackScale: 1,
    comboHitstunScale: 1,
    ledgeId: null,
    ledgeHangTimer: 0,
    ledgeGrabCooldown: 0,
    heldFighter: null,
    grabbedBy: null,
  };
}

function overlaps(
  ax: number,
  ay: number,
  aw: number,
  ah: number,
  bx: number,
  by: number,
  bw: number,
  bh: number,
): boolean {
  return (
    Math.abs(ax - bx) * 2 < aw + bw &&
    Math.abs(ay - by) * 2 < ah + bh
  );
}

function approach(value: number, target: number, amount: number): number {
  if (value < target) {
    return Math.min(value + amount, target);
  }
  return Math.max(value - amount, target);
}

function getBufferableAction(input: InputFrame): BufferedAction | null {
  if (input.shieldPressed) {
    return "shield";
  }
  if (input.jumpPressed) {
    return "jump";
  }
  if (input.attackPressed) {
    return "attack";
  }
  if (input.grabPressed) {
    return "grab";
  }
  if (input.specialPressed) {
    return "special";
  }
  return null;
}

function withBufferedAction(
  input: InputFrame,
  action: BufferedAction,
): InputFrame {
  const buffered = { ...input };
  if (action === "shield") {
    buffered.shieldPressed = true;
  } else if (action === "jump") {
    buffered.jumpPressed = true;
  } else if (action === "attack") {
    buffered.attackPressed = true;
  } else if (action === "grab") {
    buffered.grabPressed = true;
  } else {
    buffered.specialPressed = true;
  }
  return buffered;
}

export class GameSimulation {
  readonly fighters: Fighter[] = [];
  readonly projectiles: Projectile[] = [];
  playerId: FighterId = "dog";
  cpuId: FighterId = "tiger";
  status: GameStatus = "title";
  timeRemaining = MATCH_SECONDS;
  countdown = 0;
  hitStop = 0;
  winner: FighterId | null = null;
  cpuEnabled = true;
  frameNumber = 0;
  private readonly events: GameEvent[] = [];
  private projectileId = 0;
  private actionInstanceId = 0;
  private previousCountdownBeat = -1;
  private cpuThinkTimer = 0;
  private cpuAttackCooldown = 0;
  private cpuSeed = DEFAULT_CPU_SEED;
  private readonly stateAges = new Map<
    FighterId,
    { state: FighterState; frames: number }
  >();

  constructor() {
    this.resetFighters();
  }

  setPlayer(fighter: FighterId): void {
    this.setMatchup(fighter, fighter === "dog" ? "tiger" : "dog");
  }

  setMatchup(player: FighterId, cpu: FighterId): void {
    if (player === cpu) {
      throw new Error("A duel requires two different fighters.");
    }
    this.playerId = player;
    this.cpuId = cpu;
  }

  getFighter(id: FighterId): Fighter {
    const fighter = this.fighters.find((candidate) => candidate.id === id);
    if (!fighter) {
      throw new Error(`Fighter ${id} is not active in this duel.`);
    }
    return fighter;
  }

  clearComboScaling(id: FighterId): void {
    this.resetComboState(this.getFighter(id));
  }

  stageLedgeScenario(
    id: FighterId = this.playerId,
    ledgeId: LedgeId = "main-right",
  ): void {
    const fighter = this.getFighter(id);
    const ledge = this.getLedge(ledgeId);
    if (!ledge) {
      throw new Error(`Unknown ledge ${ledgeId}.`);
    }
    this.breakGrab(fighter);
    const stagedX = ledge.x - ledge.inward * 38;
    const stagedY = ledge.y + fighter.config.height * 0.55;
    fighter.position.set(stagedX, stagedY);
    fighter.previousPosition.copy(fighter.position);
    fighter.velocity.set(0, 120);
    fighter.facing = ledge.inward;
    fighter.grounded = false;
    fighter.groundPlatform = null;
    fighter.jumpsRemaining = 2;
    fighter.state = "jump";
    fighter.actionTimer = 0;
    fighter.actionDuration = 0;
    fighter.actionInstance = -1;
    fighter.actionTriggered = false;
    fighter.attackConnected = false;
    fighter.invulnerability = 0;
    fighter.respawnTimer = 0;
    fighter.dropTimer = 0;
    fighter.airDodgeAvailable = true;
    fighter.bufferedAction = null;
    fighter.bufferedActionFrames = 0;
    fighter.ledgeId = null;
    fighter.ledgeHangTimer = 0;
    fighter.ledgeGrabCooldown = 0;
    this.resetComboState(fighter);
    this.stateAges.set(fighter.id, { state: fighter.state, frames: 0 });
  }

  private resetFighters(): void {
    this.fighters.length = 0;
    this.fighters.push(
      makeFighter(this.playerId, 650, 1),
      makeFighter(this.cpuId, 1270, -1),
    );
    this.resetStateAges();
  }

  reset(): void {
    this.resetFighters();
    this.projectiles.length = 0;
    this.events.length = 0;
    this.timeRemaining = MATCH_SECONDS;
    this.countdown = 3.65;
    this.previousCountdownBeat = 4;
    this.hitStop = 0;
    this.winner = null;
    this.cpuEnabled = true;
    this.frameNumber = 0;
    this.projectileId = 0;
    this.actionInstanceId = 0;
    this.cpuThinkTimer = 0;
    this.cpuAttackCooldown = 1.4;
    this.cpuSeed = DEFAULT_CPU_SEED;
    this.status = "countdown";
  }

  pause(): void {
    if (this.status === "playing" || this.status === "countdown") {
      this.status = "paused";
    }
  }

  resume(): void {
    if (this.status === "paused") {
      this.status = this.countdown > 0 ? "countdown" : "playing";
    }
  }

  drainEvents(): GameEvent[] {
    return this.events.splice(0);
  }

  setRandomSeed(seed: number): void {
    if (!Number.isFinite(seed)) {
      throw new Error("Simulation seed must be finite.");
    }
    this.cpuSeed = seed >>> 0;
  }

  getRandomSeed(): number {
    return this.cpuSeed;
  }

  stepPausedFrame(playerInput: InputFrame): void {
    if (this.status !== "paused") {
      throw new Error("Frame stepping is only available while paused.");
    }
    this.status = this.countdown > 0 ? "countdown" : "playing";
    this.step(FIXED_STEP, playerInput);
    if ((this.status as GameStatus) !== "gameover") {
      this.status = "paused";
    }
  }

  resetTrainingPositions(): void {
    const center = WORLD_WIDTH / 2;
    this.projectiles.length = 0;
    this.projectileId = 0;
    this.actionInstanceId = 0;
    this.hitStop = 0;
    this.fighters.forEach((fighter, index) => {
      fighter.position.set(center + (index === 0 ? -110 : 110), PLATFORMS[0]!.y);
      fighter.previousPosition.copy(fighter.position);
      fighter.velocity.set(0, 0);
      fighter.facing = index === 0 ? 1 : -1;
      fighter.grounded = true;
      fighter.groundPlatform = "main";
      fighter.jumpsRemaining = 1;
      fighter.state = "idle";
      fighter.actionTimer = 0;
      fighter.actionDuration = 0;
      fighter.actionInstance = -1;
      fighter.actionTriggered = false;
      fighter.attackConnected = false;
      fighter.invulnerability = 0;
      fighter.respawnTimer = 0;
      fighter.dropTimer = 0;
      fighter.airDodgeAvailable = true;
      fighter.flashTimer = 0;
      fighter.hitPulse = 0;
      fighter.bufferedAction = null;
      fighter.bufferedActionFrames = 0;
      this.resetComboState(fighter);
      fighter.ledgeId = null;
      fighter.ledgeHangTimer = 0;
      fighter.ledgeGrabCooldown = 0;
      fighter.heldFighter = null;
      fighter.grabbedBy = null;
    });
    this.resetStateAges();
    this.events.length = 0;
  }

  step(delta: number, playerInput: InputFrame): void {
    if (Math.abs(delta - FIXED_STEP) > Number.EPSILON) {
      throw new Error(
        `GameSimulation requires a fixed ${FIXED_STEP}s step; received ${delta}.`,
      );
    }
    if (this.status === "title" || this.status === "paused" || this.status === "gameover") {
      return;
    }

    this.frameNumber += 1;
    try {
      if (this.hitStop > 0) {
        this.hitStop = Math.max(0, this.hitStop - delta);
        return;
      }

      if (this.status === "countdown") {
        this.updateCountdown(delta);
        this.updatePassiveTimers(delta);
        return;
      }

      this.timeRemaining = Math.max(0, this.timeRemaining - delta);
      if (this.timeRemaining <= 0) {
        const player = this.getFighter(this.playerId);
        const cpu = this.getFighter(this.cpuId);
        const winner =
          player.stocks !== cpu.stocks
            ? player.stocks > cpu.stocks
              ? player.id
              : cpu.id
            : player.damage <= cpu.damage
              ? player.id
              : cpu.id;
        this.finish(winner);
        return;
      }

      this.cpuAttackCooldown = Math.max(0, this.cpuAttackCooldown - delta);
      const cpuInput = this.cpuEnabled ? this.buildCpuInput(delta) : EMPTY_INPUT;
      this.updateFighter(this.getFighter(this.playerId), playerInput, delta);
      this.updateFighter(this.getFighter(this.cpuId), cpuInput, delta);
      this.resolveFighterPush();
      this.resolveCombat();
      this.updateProjectiles(delta);
      this.checkBlastZones();
      this.updateComboRecovery();
    } finally {
      this.updateStateAges();
    }
  }

  private updateCountdown(delta: number): void {
    this.countdown = Math.max(0, this.countdown - delta);
    const beat = Math.ceil(this.countdown);
    if (beat !== this.previousCountdownBeat && beat > 0 && beat <= 3) {
      this.previousCountdownBeat = beat;
      this.events.push({ type: "announce", text: String(beat) });
    }
    if (this.countdown <= 0) {
      this.status = "playing";
      this.events.push({ type: "announce", text: "FIGHT" });
    }
  }

  private updatePassiveTimers(delta: number): void {
    for (const fighter of this.fighters) {
      fighter.flashTimer = Math.max(0, fighter.flashTimer - delta);
      fighter.invulnerability = Math.max(0, fighter.invulnerability - delta);
    }
  }

  private buildCpuInput(delta: number): InputFrame {
    const cpu = this.getFighter(this.cpuId);
    const player = this.getFighter(this.playerId);
    const frame: InputFrame = { ...EMPTY_INPUT };
    if (cpu.respawnTimer > 0 || cpu.grabbedBy) {
      return frame;
    }

    if (cpu.state === "ledge-hang") {
      const stateAge = this.stateAges.get(cpu.id)?.frames ?? 0;
      if (stateAge < 18) {
        return frame;
      }
      const ledge = this.getLedge(cpu.ledgeId);
      const choice = this.random();
      if (cpu.damage >= 100 || choice < 0.24) {
        frame.shieldPressed = true;
      } else if (choice < 0.58) {
        frame.jumpPressed = true;
      } else if (choice < 0.78) {
        frame.attackPressed = true;
      } else if (ledge) {
        frame.moveX = ledge.inward;
      }
      return frame;
    }

    if (isLedgeState(cpu.state)) {
      return frame;
    }

    if (cpu.heldFighter) {
      const heldFor = GRAB_HOLD_SECONDS - cpu.actionTimer;
      if (heldFor >= 0.24) {
        frame.moveX = cpu.facing;
      }
      return frame;
    }

    this.cpuThinkTimer -= delta;
    const dx = player.position.x - cpu.position.x;
    const dy = player.position.y - cpu.position.y;
    const distance = Math.abs(dx);
    const toward: -1 | 1 = dx < 0 ? -1 : 1;

    if (cpu.position.y > 810 || cpu.position.x < 250 || cpu.position.x > 1670) {
      frame.moveX = cpu.position.x < WORLD_WIDTH / 2 ? 1 : -1;
      frame.dashHeld = true;
      frame.jumpPressed = cpu.jumpsRemaining > 0;
      return frame;
    }

    if (distance > 132) {
      frame.moveX = toward;
      frame.dashHeld = distance > 330;
    } else if (cpu.damage > 95 && this.random() < 0.18) {
      frame.moveX = toward === 1 ? -1 : 1;
    }

    if (dy < -125 && cpu.grounded && this.random() < 0.055) {
      frame.jumpPressed = true;
    }
    if (!cpu.grounded && dy < -90 && cpu.jumpsRemaining > 0 && this.random() < 0.025) {
      frame.jumpPressed = true;
    }

    if (
      player.state === "attack" &&
      distance < 190 &&
      Math.abs(dy) < 130 &&
      this.random() < 0.22
    ) {
      frame.shieldHeld = true;
      frame.shieldPressed = this.random() < 0.05;
    }

    if (this.cpuThinkTimer <= 0 && this.cpuAttackCooldown <= 0) {
      this.cpuThinkTimer = 0.12 + this.random() * 0.18;
      if (distance < 118 && Math.abs(dy) < 135) {
        const roll = this.random();
        frame.grabPressed = player.state === "shield" || roll < 0.22;
        frame.attackPressed = !frame.grabPressed;
        this.cpuAttackCooldown = 0.3 + this.random() * 0.25;
      } else if (distance > 235 && distance < 620 && Math.abs(dy) < 180 && this.random() < 0.34) {
        frame.specialPressed = true;
        this.cpuAttackCooldown = 0.75;
      }
    }

    return frame;
  }

  private random(): number {
    this.cpuSeed = (this.cpuSeed * 1664525 + 1013904223) >>> 0;
    return this.cpuSeed / 0x1_0000_0000;
  }

  private updateFighter(fighter: Fighter, input: InputFrame, delta: number): void {
    const wasGrounded = fighter.grounded;
    fighter.previousPosition.copy(fighter.position);
    this.advanceRecoveryBuffer(fighter);
    fighter.invulnerability = Math.max(0, fighter.invulnerability - delta);
    fighter.flashTimer = Math.max(0, fighter.flashTimer - delta);
    fighter.hitPulse = Math.max(0, fighter.hitPulse - delta);
    fighter.dropTimer = Math.max(0, fighter.dropTimer - delta);
    fighter.ledgeGrabCooldown = Math.max(
      0,
      fighter.ledgeGrabCooldown - delta,
    );

    if (fighter.respawnTimer > 0) {
      fighter.respawnTimer = Math.max(0, fighter.respawnTimer - delta);
      if (fighter.respawnTimer <= 0 && fighter.stocks > 0) {
        fighter.position.set(fighter.id === this.playerId ? 700 : 1220, 235);
        fighter.previousPosition.copy(fighter.position);
        fighter.velocity.set(0, 80);
        fighter.invulnerability = 1.85;
        fighter.state = "jump";
        fighter.jumpsRemaining = 1;
        fighter.ledgeId = null;
        fighter.ledgeHangTimer = 0;
        fighter.ledgeGrabCooldown = 0;
        this.events.push({
          type: "respawn",
          fighter: fighter.id,
          x: fighter.position.x,
          y: fighter.position.y,
        });
      }
      return;
    }

    if (fighter.grabbedBy) {
      this.syncGrabbedFighter(fighter);
      return;
    }

    if (fighter.heldFighter) {
      this.updateGrabHold(fighter, input, delta);
      return;
    }

    if (isLedgeState(fighter.state)) {
      this.updateLedgeState(fighter, input, delta);
      return;
    }

    if (fighter.actionTimer > 0) {
      fighter.actionTimer = Math.max(0, fighter.actionTimer - delta);
      this.triggerTimedAction(fighter);
      if (fighter.actionTimer <= 0) {
        fighter.actionTriggered = false;
        fighter.attackConnected = false;
      }
    }

    const actionLocked =
      fighter.actionTimer > 0 &&
      (fighter.state === "attack" ||
        fighter.state === "special" ||
        fighter.state === "grab" ||
        fighter.state === "hurt");

    this.captureRecoveryBuffer(fighter, input, actionLocked);
    const effectiveInput = actionLocked
      ? input
      : this.consumeRecoveryBuffer(fighter, input);

    if (!actionLocked) {
      this.handleDefense(fighter, effectiveInput);
      this.handleActionStart(fighter, effectiveInput);
    }

    const isShielding =
      fighter.state === "shield" &&
      fighter.grounded &&
      (effectiveInput.shieldHeld || fighter.actionTimer > 0);
    const canMove = !actionLocked && !isShielding;
    if (canMove) {
      this.applyMovement(fighter, effectiveInput, delta);
    } else {
      fighter.velocity.x = approach(
        fighter.velocity.x,
        0,
        (fighter.grounded ? 1700 : 420) * delta,
      );
    }

    if (!fighter.grounded) {
      fighter.velocity.y = Math.min(MAX_FALL_SPEED, fighter.velocity.y + GRAVITY * delta);
    }

    fighter.position.x += fighter.velocity.x * delta;
    fighter.position.y += fighter.velocity.y * delta;
    this.resolvePlatforms(fighter, effectiveInput);

    if (
      wasGrounded &&
      !fighter.grounded &&
      fighter.velocity.y >= 0
    ) {
      fighter.jumpsRemaining = 2;
    }

    if (!fighter.grounded && this.tryGrabLedge(fighter)) {
      return;
    }

    if (
      fighter.actionTimer <= 0 &&
      !(fighter.state === "shield" && isShielding)
    ) {
      fighter.state = !fighter.grounded
        ? "jump"
        : Math.abs(fighter.velocity.x) > 60
          ? "run"
          : "idle";
    }

    if (fighter.state !== "shield") {
      fighter.shield = Math.min(100, fighter.shield + 10 * delta);
    }
  }

  private advanceRecoveryBuffer(fighter: Fighter): void {
    if (fighter.bufferedActionFrames <= 0) {
      this.clearRecoveryBuffer(fighter);
      return;
    }
    fighter.bufferedActionFrames -= 1;
    if (fighter.bufferedActionFrames <= 0) {
      this.clearRecoveryBuffer(fighter);
    }
  }

  private captureRecoveryBuffer(
    fighter: Fighter,
    input: InputFrame,
    actionLocked: boolean,
  ): void {
    if (!actionLocked || fighter.state !== "hurt") {
      return;
    }
    const hitstunFrames = Math.max(
      0,
      Math.ceil(fighter.actionTimer / FIXED_STEP),
    );
    if (hitstunFrames > RECOVERY_INPUT_BUFFER_FRAMES) {
      return;
    }
    const action = getBufferableAction(input);
    if (!action) {
      return;
    }
    fighter.bufferedAction = action;
    fighter.bufferedActionFrames = RECOVERY_INPUT_BUFFER_FRAMES;
  }

  private consumeRecoveryBuffer(
    fighter: Fighter,
    input: InputFrame,
  ): InputFrame {
    if (getBufferableAction(input)) {
      this.clearRecoveryBuffer(fighter);
      return input;
    }
    const action = fighter.bufferedAction;
    if (!action || fighter.bufferedActionFrames <= 0) {
      return input;
    }
    const bufferedInput = withBufferedAction(input, action);
    this.clearRecoveryBuffer(fighter);
    return bufferedInput;
  }

  private clearRecoveryBuffer(fighter: Fighter): void {
    fighter.bufferedAction = null;
    fighter.bufferedActionFrames = 0;
  }

  private registerComboAction(
    defender: Fighter,
    actionInstance: number,
  ): ComboScaling {
    if (defender.comboActionInstance !== actionInstance) {
      defender.comboCount += 1;
      defender.comboActionInstance = actionInstance;
    }
    const scaling = comboScalingForStep(defender.comboCount);
    defender.comboRecoveryFrames = 0;
    defender.comboDamageScale = scaling.damage;
    defender.comboKnockbackScale = scaling.knockback;
    defender.comboHitstunScale = scaling.hitstun;
    return scaling;
  }

  private resetComboState(fighter: Fighter): void {
    fighter.comboCount = 0;
    fighter.comboRecoveryFrames = 0;
    fighter.comboActionInstance = -1;
    fighter.comboDamageScale = 1;
    fighter.comboKnockbackScale = 1;
    fighter.comboHitstunScale = 1;
  }

  private updateComboRecovery(): void {
    for (const fighter of this.fighters) {
      if (fighter.comboCount <= 0) {
        fighter.comboRecoveryFrames = 0;
        continue;
      }
      if (
        fighter.state === "hurt" ||
        fighter.grabbedBy !== null ||
        fighter.respawnTimer > 0
      ) {
        fighter.comboRecoveryFrames = 0;
        continue;
      }
      fighter.comboRecoveryFrames += 1;
      if (fighter.comboRecoveryFrames >= COMBO_RESET_FRAMES) {
        this.resetComboState(fighter);
      }
    }
  }

  private handleDefense(fighter: Fighter, input: InputFrame): void {
    if (
      (input.shieldHeld || input.shieldPressed) &&
      fighter.grounded &&
      fighter.shield > 0
    ) {
      fighter.state = "shield";
      fighter.actionTimer = input.shieldHeld
        ? 0
        : Math.max(fighter.actionTimer, 0.12);
      fighter.shield = Math.max(0, fighter.shield - 17 * FIXED_STEP);
      if (input.shieldPressed) {
        this.events.push({
          type: "shield",
          fighter: fighter.id,
          x: fighter.position.x,
          y: fighter.position.y - fighter.config.height * 0.5,
        });
      }
      if (fighter.shield <= 0) {
        this.clearRecoveryBuffer(fighter);
        fighter.state = "hurt";
        fighter.actionDuration = 1.2;
        fighter.actionTimer = 1.2;
        fighter.velocity.y = -220;
        this.events.push({
          type: "shield-break",
          fighter: fighter.id,
          x: fighter.position.x,
          y: fighter.position.y - fighter.config.height * 0.55,
        });
      }
      return;
    }

    if (
      input.shieldPressed &&
      !fighter.grounded &&
      fighter.airDodgeAvailable &&
      fighter.actionTimer <= 0
    ) {
      fighter.state = "shield";
      fighter.actionDuration = 0.34;
      fighter.actionTimer = 0.34;
      fighter.invulnerability = Math.max(fighter.invulnerability, 0.3);
      fighter.airDodgeAvailable = false;
      fighter.velocity.x = input.moveX * 570;
      fighter.velocity.y = -80;
      this.events.push({
        type: "shield",
        fighter: fighter.id,
        x: fighter.position.x,
        y: fighter.position.y - fighter.config.height * 0.5,
      });
    }
  }

  private handleActionStart(fighter: Fighter, input: InputFrame): void {
    if (fighter.actionTimer > 0) {
      return;
    }
    if (input.attackPressed) {
      this.startAction(fighter, "attack", 0.34);
      this.events.push({
        type: "attack",
        fighter: fighter.id,
        x: fighter.position.x,
        y: fighter.position.y - fighter.config.height * 0.55,
      });
      return;
    }
    if (input.specialPressed) {
      this.startAction(fighter, "special", fighter.config.specialDuration);
      return;
    }
    if (input.grabPressed && fighter.grounded) {
      this.startAction(fighter, "grab", 0.42);
    }
  }

  private updateGrabHold(
    attacker: Fighter,
    input: InputFrame,
    delta: number,
  ): void {
    const defenderId = attacker.heldFighter;
    if (!defenderId) {
      return;
    }
    const defender = this.getFighter(defenderId);
    if (
      defender.grabbedBy !== attacker.id ||
      attacker.respawnTimer > 0 ||
      defender.respawnTimer > 0
    ) {
      this.breakGrab(attacker);
      return;
    }

    attacker.state = "grab";
    attacker.velocity.set(0, 0);
    attacker.actionTimer = Math.max(0, attacker.actionTimer - delta);
    const heldFor = GRAB_HOLD_SECONDS - attacker.actionTimer;
    this.syncGrabbedFighter(defender);

    const backDirection: -1 | 1 = attacker.facing === 1 ? -1 : 1;
    if (input.moveX === backDirection) {
      attacker.facing = backDirection;
      this.syncGrabbedFighter(defender);
      this.throwHeldFighter(attacker, backDirection, false);
      return;
    }

    if (heldFor < GRAB_INPUT_DELAY) {
      return;
    }

    if (input.moveX !== 0) {
      this.throwHeldFighter(attacker, input.moveX);
      return;
    }

    if (input.attackPressed || input.grabPressed || attacker.actionTimer <= 0) {
      this.throwHeldFighter(attacker, attacker.facing);
    }
  }

  private syncGrabbedFighter(defender: Fighter): void {
    const attackerId = defender.grabbedBy;
    if (!attackerId) {
      return;
    }
    const attacker = this.getFighter(attackerId);
    if (attacker.heldFighter !== defender.id) {
      defender.grabbedBy = null;
      return;
    }

    const holdDistance =
      attacker.config.width * 0.42 + defender.config.width * 0.22;
    defender.position.x = attacker.position.x + attacker.facing * holdDistance;
    defender.position.y = attacker.position.y;
    defender.velocity.set(0, 0);
    defender.grounded = attacker.grounded;
    defender.groundPlatform = attacker.groundPlatform;
    defender.state = "hurt";
    defender.actionDuration = GRAB_HOLD_SECONDS;
    defender.actionTimer = attacker.actionTimer;
  }

  private startAction(fighter: Fighter, state: FighterState, duration: number): void {
    fighter.state = state;
    fighter.actionDuration = duration;
    fighter.actionTimer = duration;
    fighter.actionInstance = this.actionInstanceId;
    this.actionInstanceId += 1;
    fighter.actionTriggered = false;
    fighter.attackConnected = false;
  }

  private triggerTimedAction(fighter: Fighter): void {
    if (fighter.state !== "special" || fighter.actionTriggered) {
      return;
    }
    const elapsed = fighter.actionDuration - fighter.actionTimer;
    const triggerTime = fighter.config.specialTriggerTime;
    if (elapsed < triggerTime) {
      return;
    }

    fighter.actionTriggered = true;
    const direction = fighter.facing;
    const projectile: Projectile = {
      id: this.projectileId,
      actionInstance: fighter.actionInstance,
      owner: fighter.id,
      position: new Vector2(
        fighter.position.x + direction * fighter.config.projectileOffset,
        fighter.position.y - fighter.config.height * 0.58,
      ),
      velocity: new Vector2(direction * fighter.config.projectileSpeed, -18),
      radius: fighter.config.projectileRadius,
      damage: fighter.config.specialDamage,
      power: fighter.config.specialPower,
      life: fighter.config.projectileLife,
      color: fighter.config.projectileColor,
    };
    this.projectileId += 1;
    this.projectiles.push(projectile);
    this.events.push({
      type: "special",
      fighter: fighter.id,
      x: projectile.position.x,
      y: projectile.position.y,
    });
  }

  private getLedge(id: LedgeId | null): StageLedge | null {
    if (!id) {
      return null;
    }
    return LEDGES.find((ledge) => ledge.id === id) ?? null;
  }

  private ledgeAnchor(
    fighter: Fighter,
    ledge: StageLedge,
  ): { x: number; y: number } {
    return {
      x: ledge.x - ledge.inward * fighter.config.width * 0.28,
      y: ledge.y + fighter.config.height * 0.58,
    };
  }

  private ledgeTargetX(
    fighter: Fighter,
    ledge: StageLedge,
    state: FighterState,
  ): number {
    const distance =
      state === "ledge-roll"
        ? fighter.config.width * 2.35 + 45
        : state === "ledge-attack"
          ? fighter.config.width * 0.95 + 22
          : fighter.config.width * 0.76 + 16;
    return ledge.x + ledge.inward * distance;
  }

  private tryGrabLedge(fighter: Fighter): boolean {
    if (
      fighter.grounded ||
      fighter.respawnTimer > 0 ||
      fighter.grabbedBy ||
      fighter.heldFighter ||
      fighter.ledgeGrabCooldown > 0 ||
      fighter.velocity.y < LEDGE_GRAB_MAX_RISE_SPEED ||
      (fighter.state !== "jump" && fighter.state !== "hurt")
    ) {
      return false;
    }

    for (const ledge of LEDGES) {
      if (fighter.facing !== ledge.inward) {
        continue;
      }
      const occupied = this.fighters.some(
        (candidate) =>
          candidate.id !== fighter.id &&
          candidate.ledgeId === ledge.id &&
          isLedgeState(candidate.state),
      );
      if (occupied) {
        continue;
      }

      const grabPointX =
        fighter.position.x + fighter.facing * fighter.config.width * 0.32;
      const grabPointY =
        fighter.position.y - fighter.config.height * 0.55;
      const verticalOffset = grabPointY - ledge.y;
      const outsideEnough =
        ledge.inward === 1
          ? fighter.position.x <= ledge.x + fighter.config.width * 0.18
          : fighter.position.x >= ledge.x - fighter.config.width * 0.18;
      if (
        !outsideEnough ||
        Math.abs(grabPointX - ledge.x) > LEDGE_GRAB_HORIZONTAL_RANGE ||
        verticalOffset < -LEDGE_GRAB_ABOVE_RANGE ||
        verticalOffset > LEDGE_GRAB_BELOW_RANGE
      ) {
        continue;
      }

      const anchor = this.ledgeAnchor(fighter, ledge);
      fighter.position.set(anchor.x, anchor.y);
      fighter.previousPosition.copy(fighter.position);
      fighter.velocity.set(0, 0);
      fighter.facing = ledge.inward;
      fighter.grounded = false;
      fighter.groundPlatform = null;
      fighter.jumpsRemaining = 2;
      fighter.airDodgeAvailable = true;
      fighter.state = "ledge-hang";
      fighter.actionTimer = 0;
      fighter.actionDuration = 0;
      fighter.actionInstance = -1;
      fighter.actionTriggered = false;
      fighter.attackConnected = false;
      fighter.ledgeId = ledge.id;
      fighter.ledgeHangTimer = fighter.damage >= 100 ? 8 : 18;
      fighter.invulnerability = Math.max(
        fighter.invulnerability,
        LEDGE_GRAB_INVULNERABILITY,
      );
      this.clearRecoveryBuffer(fighter);
      this.events.push({
        type: "ledge-grab",
        fighter: fighter.id,
        ledge: ledge.id,
        x: ledge.x,
        y: ledge.y,
      });
      return true;
    }

    return false;
  }

  private updateLedgeState(
    fighter: Fighter,
    input: InputFrame,
    delta: number,
  ): void {
    const ledge = this.getLedge(fighter.ledgeId);
    if (!ledge) {
      fighter.state = "jump";
      fighter.ledgeId = null;
      fighter.ledgeHangTimer = 0;
      return;
    }

    fighter.grounded = false;
    fighter.groundPlatform = null;
    fighter.facing = ledge.inward;
    fighter.velocity.set(0, 0);

    if (fighter.state === "ledge-hang") {
      const anchor = this.ledgeAnchor(fighter, ledge);
      fighter.position.set(anchor.x, anchor.y);
      fighter.ledgeHangTimer = Math.max(
        0,
        fighter.ledgeHangTimer - delta,
      );

      if (input.attackPressed) {
        this.startLedgeAction(
          fighter,
          "ledge-attack",
          LEDGE_ATTACK_DURATION,
        );
        this.events.push({
          type: "attack",
          fighter: fighter.id,
          x: ledge.x,
          y: ledge.y,
        });
        return;
      }
      if (input.shieldPressed) {
        this.startLedgeAction(
          fighter,
          "ledge-roll",
          LEDGE_ROLL_DURATION,
        );
        fighter.invulnerability = Math.max(
          fighter.invulnerability,
          LEDGE_ROLL_INVULNERABILITY,
        );
        return;
      }
      if (input.jumpPressed) {
        this.releaseLedgeToAir(fighter, ledge, true);
        return;
      }
      if (
        input.downHeld ||
        input.moveX === -ledge.inward ||
        fighter.ledgeHangTimer <= 0
      ) {
        this.releaseLedgeToAir(fighter, ledge, false);
        return;
      }
      if (input.moveX === ledge.inward || input.grabPressed) {
        this.startLedgeAction(
          fighter,
          "ledge-climb",
          LEDGE_CLIMB_DURATION,
        );
      }
      return;
    }

    fighter.actionTimer = Math.max(0, fighter.actionTimer - delta);
    const progress =
      fighter.actionDuration > 0
        ? Math.min(
            1,
            Math.max(
              0,
              1 - fighter.actionTimer / fighter.actionDuration,
            ),
          )
        : 1;
    const eased = 1 - (1 - progress) ** 2;
    const anchor = this.ledgeAnchor(fighter, ledge);
    const targetX = this.ledgeTargetX(fighter, ledge, fighter.state);
    fighter.position.x = anchor.x + (targetX - anchor.x) * eased;
    fighter.position.y = anchor.y + (ledge.y - anchor.y) * eased;

    if (fighter.actionTimer <= 0) {
      this.finishLedgeAction(fighter, ledge);
    }
  }

  private startLedgeAction(
    fighter: Fighter,
    state: "ledge-climb" | "ledge-attack" | "ledge-roll",
    duration: number,
  ): void {
    fighter.state = state;
    fighter.actionDuration = duration;
    fighter.actionTimer = duration;
    fighter.actionInstance = this.actionInstanceId;
    this.actionInstanceId += 1;
    fighter.actionTriggered = false;
    fighter.attackConnected = false;
  }

  private finishLedgeAction(
    fighter: Fighter,
    ledge: StageLedge,
  ): void {
    fighter.position.set(
      this.ledgeTargetX(fighter, ledge, fighter.state),
      ledge.y,
    );
    fighter.previousPosition.copy(fighter.position);
    fighter.velocity.set(0, 0);
    fighter.grounded = true;
    fighter.groundPlatform = ledge.platformId;
    fighter.jumpsRemaining = 1;
    fighter.airDodgeAvailable = true;
    fighter.state = "idle";
    fighter.actionTimer = 0;
    fighter.actionDuration = 0;
    fighter.actionTriggered = false;
    fighter.attackConnected = false;
    fighter.ledgeId = null;
    fighter.ledgeHangTimer = 0;
    fighter.ledgeGrabCooldown = LEDGE_REGRAB_COOLDOWN;
  }

  private releaseLedgeToAir(
    fighter: Fighter,
    ledge: StageLedge,
    jumping: boolean,
  ): void {
    fighter.ledgeId = null;
    fighter.ledgeHangTimer = 0;
    fighter.ledgeGrabCooldown = LEDGE_REGRAB_COOLDOWN;
    fighter.grounded = false;
    fighter.groundPlatform = null;
    fighter.state = "jump";
    fighter.actionTimer = 0;
    fighter.actionDuration = 0;
    fighter.actionTriggered = false;
    fighter.attackConnected = false;
    fighter.airDodgeAvailable = true;

    if (jumping) {
      fighter.facing = ledge.inward;
      fighter.velocity.x = ledge.inward * 145;
      fighter.velocity.y = -fighter.config.jumpSpeed;
      fighter.jumpsRemaining = 1;
      this.events.push({
        type: "jump",
        fighter: fighter.id,
        x: fighter.position.x,
        y: fighter.position.y,
      });
      return;
    }

    fighter.velocity.x = -ledge.inward * 70;
    fighter.velocity.y = 110;
    fighter.jumpsRemaining = 2;
    fighter.invulnerability = 0;
  }

  private clearLedgeState(fighter: Fighter): void {
    fighter.ledgeId = null;
    fighter.ledgeHangTimer = 0;
    fighter.ledgeGrabCooldown = LEDGE_REGRAB_COOLDOWN;
  }

  private applyMovement(fighter: Fighter, input: InputFrame, delta: number): void {
    if (input.moveX !== 0) {
      fighter.facing = input.moveX;
    }

    const maxSpeed = fighter.grounded
      ? input.dashHeld
        ? fighter.config.dashSpeed
        : fighter.config.moveSpeed
      : fighter.config.airSpeed;
    const target = input.moveX * maxSpeed;
    const acceleration = fighter.grounded ? 2500 : 1050;
    const friction = fighter.grounded && input.moveX === 0 ? 3150 : acceleration;
    fighter.velocity.x = approach(fighter.velocity.x, target, friction * delta);

    if (input.downHeld && fighter.groundPlatform && fighter.groundPlatform !== "main") {
      fighter.dropTimer = 0.24;
      fighter.grounded = false;
      fighter.groundPlatform = null;
      fighter.position.y += 7;
      fighter.velocity.y = Math.max(120, fighter.velocity.y);
    }

    if (input.jumpPressed) {
      if (fighter.grounded) {
        fighter.grounded = false;
        fighter.groundPlatform = null;
        fighter.velocity.y = -fighter.config.jumpSpeed;
        fighter.jumpsRemaining = 1;
        fighter.airDodgeAvailable = true;
        fighter.state = "jump";
        this.events.push({
          type: "jump",
          fighter: fighter.id,
          x: fighter.position.x,
          y: fighter.position.y,
        });
      } else if (fighter.jumpsRemaining > 0) {
        const walkOffJump = fighter.jumpsRemaining === 2;
        fighter.jumpsRemaining -= 1;
        fighter.velocity.y =
          -fighter.config.jumpSpeed * (walkOffJump ? 1 : 0.88);
        fighter.state = "jump";
        this.events.push({
          type: "jump",
          fighter: fighter.id,
          x: fighter.position.x,
          y: fighter.position.y,
        });
      }
    }
  }

  private resolvePlatforms(fighter: Fighter, input: InputFrame): void {
    fighter.grounded = false;
    fighter.groundPlatform = null;
    if (fighter.velocity.y < 0) {
      return;
    }

    let landing: Platform | null = null;
    for (const platform of PLATFORMS) {
      if (
        platform.id !== "main" &&
        (fighter.dropTimer > 0 || input.downHeld)
      ) {
        continue;
      }
      const withinX =
        fighter.position.x + fighter.config.width * 0.36 >= platform.x1 &&
        fighter.position.x - fighter.config.width * 0.36 <= platform.x2;
      const crossed =
        fighter.previousPosition.y <= platform.y + 2 &&
        fighter.position.y >= platform.y;
      if (withinX && crossed && (!landing || platform.y < landing.y)) {
        landing = platform;
      }
    }

    if (landing) {
      fighter.position.y = landing.y;
      fighter.velocity.y = 0;
      fighter.grounded = true;
      fighter.groundPlatform = landing.id;
      fighter.jumpsRemaining = 1;
      fighter.airDodgeAvailable = true;
      fighter.ledgeGrabCooldown = 0;
    }
  }

  private resolveFighterPush(): void {
    const left = this.getFighter(this.playerId);
    const right = this.getFighter(this.cpuId);
    if (
      left.respawnTimer > 0 ||
      right.respawnTimer > 0 ||
      left.heldFighter ||
      right.heldFighter ||
      isLedgeState(left.state) ||
      isLedgeState(right.state)
    ) {
      return;
    }
    const verticalDistance = Math.abs(
      left.position.y - left.config.height * 0.5 -
        (right.position.y - right.config.height * 0.5),
    );
    const minimum = (left.config.width + right.config.width) * 0.42;
    const dx = right.position.x - left.position.x;
    if (verticalDistance < 120 && Math.abs(dx) < minimum) {
      const direction = dx >= 0 ? 1 : -1;
      const correction = (minimum - Math.abs(dx)) * 0.5;
      left.position.x -= direction * correction;
      right.position.x += direction * correction;
    }
  }

  private resolveCombat(): void {
    const player = this.getFighter(this.playerId);
    const cpu = this.getFighter(this.cpuId);
    this.resolveMelee(player, cpu);
    this.resolveMelee(cpu, player);
  }

  private resolveMelee(attacker: Fighter, defender: Fighter): void {
    if (
      attacker.respawnTimer > 0 ||
      defender.respawnTimer > 0 ||
      attacker.attackConnected ||
      defender.invulnerability > 0 ||
      attacker.grabbedBy ||
      defender.grabbedBy
    ) {
      return;
    }

    const elapsed = attacker.actionDuration - attacker.actionTimer;
    let active = false;
    let reach = 0;
    let height = 0;
    let damage = 0;
    let power = 0;
    let isGrab = false;

    if (attacker.state === "attack" && elapsed >= 0.1 && elapsed <= 0.235) {
      active = true;
      reach = attacker.config.attackReach;
      height = attacker.config.attackHeight;
      damage = attacker.config.attackDamage;
      power = attacker.config.attackPower;
    } else if (
      attacker.state === "ledge-attack" &&
      elapsed >= 0.14 &&
      elapsed <= 0.3
    ) {
      active = true;
      reach = attacker.config.attackReach;
      height = attacker.config.attackHeight;
      damage = attacker.config.attackDamage;
      power = attacker.config.attackPower * 0.82;
    } else if (attacker.state === "grab" && elapsed >= 0.09 && elapsed <= 0.24) {
      active = true;
      isGrab = true;
      reach = attacker.config.grabReach;
      height = 128;
    }

    if (!active) {
      return;
    }
    if (isGrab && isLedgeState(defender.state)) {
      return;
    }

    const hitX = attacker.position.x + attacker.facing * reach * 0.52;
    const hitY = attacker.position.y - attacker.config.height * 0.53;
    const bodyY = defender.position.y - defender.config.height * 0.5;
    if (
      !overlaps(
        hitX,
        hitY,
        reach,
        height,
        defender.position.x,
        bodyY,
        defender.config.width,
        defender.config.height,
      )
    ) {
      return;
    }

    attacker.attackConnected = true;
    if (isGrab) {
      this.captureGrab(attacker, defender, hitX, hitY);
    } else {
      this.applyHit(
        attacker,
        defender,
        damage,
        power,
        attacker.actionInstance,
      );
    }
  }

  private captureGrab(
    attacker: Fighter,
    defender: Fighter,
    hitX: number,
    hitY: number,
  ): void {
    if (isLedgeState(defender.state)) {
      this.clearLedgeState(defender);
    }
    attacker.heldFighter = defender.id;
    defender.grabbedBy = attacker.id;
    attacker.state = "grab";
    attacker.actionDuration = GRAB_HOLD_SECONDS;
    attacker.actionTimer = GRAB_HOLD_SECONDS;
    attacker.actionTriggered = true;
    attacker.attackConnected = true;
    attacker.velocity.set(0, 0);
    defender.velocity.set(0, 0);
    this.clearRecoveryBuffer(defender);
    defender.comboRecoveryFrames = 0;
    defender.state = "hurt";
    defender.actionDuration = GRAB_HOLD_SECONDS;
    defender.actionTimer = GRAB_HOLD_SECONDS;
    defender.invulnerability = 0;
    this.syncGrabbedFighter(defender);
    this.hitStop = 0.035;
    this.events.push({
      type: "grab",
      attacker: attacker.id,
      defender: defender.id,
      x: hitX,
      y: hitY,
    });
  }

  private throwHeldFighter(
    attacker: Fighter,
    direction: -1 | 1,
    forward = direction === attacker.facing,
  ): void {
    const defenderId = attacker.heldFighter;
    if (!defenderId) {
      return;
    }
    const defender = this.getFighter(defenderId);
    const launchDirection = direction;
    const baseDamage = attacker.config.grabDamage + (forward ? 0 : 2);
    const power = forward
      ? attacker.config.throwPowerForward
      : attacker.config.throwPowerBack;
    const combo = this.registerComboAction(
      defender,
      attacker.actionInstance,
    );
    const damage = scaleComboDamage(baseDamage, defender.comboCount);

    attacker.heldFighter = null;
    defender.grabbedBy = null;
    defender.damage = Math.min(999, defender.damage + damage);
    const damageKnockbackScaling = 0.76 + defender.damage / 92;
    const launch =
      ((power * damageKnockbackScaling) / defender.config.weight) *
      combo.knockback;
    defender.velocity.x = launchDirection * launch;
    defender.velocity.y = -launch * 0.4;
    defender.grounded = false;
    defender.groundPlatform = null;
    this.clearRecoveryBuffer(defender);
    defender.jumpsRemaining = 1;
    defender.state = "hurt";
    defender.actionDuration = 0.44 * combo.hitstun;
    defender.actionTimer = defender.actionDuration;
    defender.invulnerability = 0.16;
    defender.flashTimer = 0.12;
    defender.hitPulse = 0.22;

    attacker.state = "grab";
    attacker.actionDuration = 0.3;
    attacker.actionTimer = 0.3;
    attacker.actionTriggered = true;
    attacker.attackConnected = true;
    attacker.velocity.x = -launchDirection * 70;

    const hitX = (attacker.position.x + defender.position.x) * 0.5;
    const hitY = defender.position.y - defender.config.height * 0.54;
    this.hitStop = Math.min(0.095, 0.05 + damage * 0.0024);
    this.events.push({
      type: "throw",
      attacker: attacker.id,
      defender: defender.id,
      x: hitX,
      y: hitY,
      direction: launchDirection,
      forward,
    });
    this.events.push({
      type: "hit",
      attacker: attacker.id,
      defender: defender.id,
      x: hitX,
      y: hitY,
      damage,
      baseDamage,
      strength: launch,
      comboCount: defender.comboCount,
      damageScale: combo.damage,
      knockbackScale: combo.knockback,
      hitstunScale: combo.hitstun,
    });
  }

  private breakGrab(fighter: Fighter): void {
    if (fighter.heldFighter) {
      const defender = this.getFighter(fighter.heldFighter);
      defender.grabbedBy = null;
      defender.actionDuration = 0.12;
      defender.actionTimer = 0.12;
      defender.state = defender.grounded ? "idle" : "jump";
      fighter.heldFighter = null;
    }

    if (fighter.grabbedBy) {
      const attacker = this.getFighter(fighter.grabbedBy);
      attacker.heldFighter = null;
      attacker.actionDuration = 0.12;
      attacker.actionTimer = 0.12;
      fighter.grabbedBy = null;
    }
  }

  private applyHit(
    attacker: Fighter,
    defender: Fighter,
    baseDamage: number,
    power: number,
    actionInstance: number,
  ): void {
    this.breakGrab(attacker);
    this.breakGrab(defender);
    if (isLedgeState(defender.state)) {
      this.clearLedgeState(defender);
    }
    const hitX = (attacker.position.x + defender.position.x) * 0.5;
    const hitY = defender.position.y - defender.config.height * 0.62;

    if (defender.state === "shield") {
      this.resetComboState(defender);
      defender.shield = Math.max(0, defender.shield - baseDamage * 3.1);
      defender.velocity.x += attacker.facing * 90;
      attacker.velocity.x -= attacker.facing * 105;
      this.hitStop = 0.035;
      this.events.push({
        type: "shield",
        fighter: defender.id,
        x: hitX,
        y: hitY,
      });
      if (defender.shield <= 0) {
        this.clearRecoveryBuffer(defender);
        defender.state = "hurt";
        defender.actionDuration = 1.15;
        defender.actionTimer = 1.15;
        this.events.push({
          type: "shield-break",
          fighter: defender.id,
          x: hitX,
          y: hitY,
        });
      }
      return;
    }

    const combo = this.registerComboAction(defender, actionInstance);
    const damage = scaleComboDamage(baseDamage, defender.comboCount);
    defender.damage = Math.min(999, defender.damage + damage);
    const damageKnockbackScaling = 0.76 + defender.damage / 92;
    const launch =
      ((power * damageKnockbackScaling) / defender.config.weight) *
      STANDARD_HIT_KNOCKBACK_SCALE *
      combo.knockback;
    defender.velocity.x = attacker.facing * launch;
    defender.velocity.y = -launch * 0.43;
    defender.grounded = false;
    defender.groundPlatform = null;
    this.clearRecoveryBuffer(defender);
    defender.jumpsRemaining = 1;
    defender.state = "hurt";
    defender.actionDuration = 0.32 * combo.hitstun;
    defender.actionTimer = defender.actionDuration;
    defender.invulnerability = 0.16;
    defender.flashTimer = 0.12;
    defender.hitPulse = 0.22;
    this.hitStop = Math.min(0.095, 0.045 + damage * 0.0024);

    this.events.push({
      type: "hit",
      attacker: attacker.id,
      defender: defender.id,
      x: hitX,
      y: hitY,
      damage,
      baseDamage,
      strength: launch,
      comboCount: defender.comboCount,
      damageScale: combo.damage,
      knockbackScale: combo.knockback,
      hitstunScale: combo.hitstun,
    });
  }

  private updateProjectiles(delta: number): void {
    for (let index = this.projectiles.length - 1; index >= 0; index -= 1) {
      const projectile = this.projectiles[index];
      if (!projectile) {
        continue;
      }
      projectile.position.x += projectile.velocity.x * delta;
      projectile.position.y += projectile.velocity.y * delta;
      projectile.life -= delta;
      const defender = this.fighters.find(
        (fighter) => fighter.id !== projectile.owner,
      );
      if (!defender) {
        this.projectiles.splice(index, 1);
        continue;
      }
      const bodyY = defender.position.y - defender.config.height * 0.5;
      const hit =
        defender.respawnTimer <= 0 &&
        defender.invulnerability <= 0 &&
        overlaps(
          projectile.position.x,
          projectile.position.y,
          projectile.radius * 2,
          projectile.radius * 2,
          defender.position.x,
          bodyY,
          defender.config.width,
          defender.config.height,
        );

      if (hit) {
        const attacker = this.getFighter(projectile.owner);
        this.applyHit(
          attacker,
          defender,
          projectile.damage,
          projectile.power,
          projectile.actionInstance,
        );
        this.projectiles.splice(index, 1);
      } else if (
        projectile.life <= 0 ||
        projectile.position.x < -150 ||
        projectile.position.x > WORLD_WIDTH + 150
      ) {
        this.projectiles.splice(index, 1);
      }
    }
  }

  private checkBlastZones(): void {
    for (const fighter of this.fighters) {
      if (fighter.respawnTimer > 0) {
        continue;
      }
      const out =
        fighter.position.x < -185 ||
        fighter.position.x > WORLD_WIDTH + 185 ||
        fighter.position.y < -290 ||
        fighter.position.y > WORLD_HEIGHT + 175;
      if (!out) {
        continue;
      }

      this.breakGrab(fighter);
      this.clearLedgeState(fighter);
      fighter.stocks -= 1;
      fighter.respawnTimer = 0.78;
      fighter.velocity.set(0, 0);
      fighter.damage = 0;
      this.clearRecoveryBuffer(fighter);
      this.resetComboState(fighter);
      this.events.push({
        type: "ko",
        fighter: fighter.id,
        x: fighter.position.x,
        y: fighter.position.y,
      });
      if (fighter.stocks <= 0) {
        const winner = this.fighters.find(
          (candidate) => candidate.id !== fighter.id,
        )?.id;
        if (!winner) {
          throw new Error("A duel cannot finish without an opposing fighter.");
        }
        this.finish(winner);
        return;
      }
    }
  }

  captureSnapshot(): SimulationSnapshot {
    return {
      playerId: this.playerId,
      cpuId: this.cpuId,
      status: this.status,
      timeRemaining: this.timeRemaining,
      countdown: this.countdown,
      hitStop: this.hitStop,
      winner: this.winner,
      cpuEnabled: this.cpuEnabled,
      frameNumber: this.frameNumber,
      projectileId: this.projectileId,
      actionInstanceId: this.actionInstanceId,
      previousCountdownBeat: this.previousCountdownBeat,
      cpuThinkTimer: this.cpuThinkTimer,
      cpuAttackCooldown: this.cpuAttackCooldown,
      cpuSeed: this.cpuSeed,
      fighters: this.fighters.map((fighter) => {
        const {
          config: _config,
          position,
          previousPosition,
          velocity,
          ...state
        } = fighter;
        return {
          ...state,
          position: { x: position.x, y: position.y },
          previousPosition: {
            x: previousPosition.x,
            y: previousPosition.y,
          },
          velocity: { x: velocity.x, y: velocity.y },
          stateAgeFrames: this.stateAges.get(fighter.id)?.frames ?? 0,
        };
      }),
      projectiles: this.projectiles.map(
        ({ position, velocity, ...projectile }) => ({
          ...projectile,
          position: { x: position.x, y: position.y },
          velocity: { x: velocity.x, y: velocity.y },
        }),
      ),
    };
  }

  restoreSnapshot(snapshot: SimulationSnapshot): void {
    if (snapshot.playerId === snapshot.cpuId) {
      throw new Error("A replay snapshot requires two different fighters.");
    }
    if (snapshot.fighters.length !== 2) {
      throw new Error("Combat recovery snapshots require exactly two fighters.");
    }

    this.playerId = snapshot.playerId;
    this.cpuId = snapshot.cpuId;
    this.status = snapshot.status;
    this.timeRemaining = snapshot.timeRemaining;
    this.countdown = snapshot.countdown;
    this.hitStop = snapshot.hitStop;
    this.winner = snapshot.winner;
    this.cpuEnabled = snapshot.cpuEnabled;
    this.frameNumber = snapshot.frameNumber;
    this.projectileId = snapshot.projectileId;
    this.actionInstanceId = snapshot.actionInstanceId;
    this.previousCountdownBeat = snapshot.previousCountdownBeat;
    this.cpuThinkTimer = snapshot.cpuThinkTimer;
    this.cpuAttackCooldown = snapshot.cpuAttackCooldown;
    this.cpuSeed = snapshot.cpuSeed >>> 0;
    this.events.length = 0;

    this.fighters.length = 0;
    this.stateAges.clear();
    for (const fighterSnapshot of snapshot.fighters) {
      const {
        position,
        previousPosition,
        velocity,
        stateAgeFrames,
        ...fighter
      } = fighterSnapshot;
      this.fighters.push({
        ...fighter,
        config: getFighterConfig(fighter.id),
        position: new Vector2(position.x, position.y),
        previousPosition: new Vector2(
          previousPosition.x,
          previousPosition.y,
        ),
        velocity: new Vector2(velocity.x, velocity.y),
      });
      this.stateAges.set(fighter.id, {
        state: fighter.state,
        frames: stateAgeFrames,
      });
    }

    this.projectiles.length = 0;
    for (const { position, velocity, ...projectile } of snapshot.projectiles) {
      this.projectiles.push({
        ...projectile,
        position: new Vector2(position.x, position.y),
        velocity: new Vector2(velocity.x, velocity.y),
      });
    }
  }

  getDeterministicState(): SimulationSnapshot {
    return this.captureSnapshot();
  }

  stateHash(): string {
    return hashDeterministicState(this.getDeterministicState());
  }

  getFighterDiagnostics(id: FighterId): FighterDiagnostics {
    const fighter = this.getFighter(id);
    const stateAgeFrames = this.stateAges.get(id)?.frames ?? 0;
    const motionOwner: FighterDiagnostics["motionOwner"] =
      fighter.respawnTimer > 0
        ? "respawn"
        : fighter.grabbedBy
          ? "captured"
          : fighter.heldFighter
            ? "grab-hold"
            : fighter.state === "ledge-hang"
              ? "ledge-hang"
              : isLedgeState(fighter.state)
                ? "ledge-action"
            : fighter.state === "hurt"
              ? "hit-reaction"
              : fighter.state === "shield"
                ? "shield"
                : fighter.state === "attack"
                  ? "attack-action"
                  : fighter.state === "special"
                    ? "special-action"
                    : fighter.state === "grab"
                      ? "grab-action"
                      : fighter.grounded
                        ? "ground-movement"
                        : "air-movement";
    return {
      id,
      state: fighter.state,
      stateAgeFrames,
      motionOwner,
      grounded: fighter.grounded,
      groundPlatform: fighter.groundPlatform,
      position: { x: fighter.position.x, y: fighter.position.y },
      velocity: { x: fighter.velocity.x, y: fighter.velocity.y },
      actionFramesRemaining: Math.max(
        0,
        Math.ceil(fighter.actionTimer / FIXED_STEP),
      ),
      hitstunFramesRemaining:
        fighter.state === "hurt"
          ? Math.max(0, Math.ceil(fighter.actionTimer / FIXED_STEP))
          : 0,
      invulnerabilityFramesRemaining: Math.max(
        0,
        Math.ceil(fighter.invulnerability / FIXED_STEP),
      ),
      bufferedAction: fighter.bufferedAction,
      bufferedActionFrames: fighter.bufferedActionFrames,
      comboCount: fighter.comboCount,
      comboRecoveryFrames: fighter.comboRecoveryFrames,
      comboDamageScale: fighter.comboDamageScale,
      comboKnockbackScale: fighter.comboKnockbackScale,
      comboHitstunScale: fighter.comboHitstunScale,
      jumpsRemaining: fighter.jumpsRemaining,
      ledgeId: fighter.ledgeId,
      ledgeHangFramesRemaining: Math.max(
        0,
        Math.ceil(fighter.ledgeHangTimer / FIXED_STEP),
      ),
      ledgeGrabCooldownFrames: Math.max(
        0,
        Math.ceil(fighter.ledgeGrabCooldown / FIXED_STEP),
      ),
      holding: fighter.heldFighter,
      grabbedBy: fighter.grabbedBy,
    };
  }

  private resetStateAges(): void {
    this.stateAges.clear();
    for (const fighter of this.fighters) {
      this.stateAges.set(fighter.id, { state: fighter.state, frames: 0 });
    }
  }

  private updateStateAges(): void {
    for (const fighter of this.fighters) {
      const previous = this.stateAges.get(fighter.id);
      this.stateAges.set(fighter.id, {
        state: fighter.state,
        frames:
          previous?.state === fighter.state ? previous.frames + 1 : 0,
      });
    }
  }

  private finish(winner: FighterId): void {
    this.winner = winner;
    this.status = "gameover";
    this.events.push({ type: "gameover", winner });
  }
}
